import { Fragment, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { clearAuth, useAuth } from '@/features/auth/authStore';
import { DASHBOARD_CONFIGS } from '@/features/dashboard/config';
import { listPipelineRuns, listPipelineSchedule, stopPipelineRun, triggerPipeline, type AdminPipelinesApiError, type PipelineRun } from '@/api/adminPipelines';
import { backendOptions } from '@/features/admin/backends';
import { Alert } from '@/components/ui/Alert';
import { Button } from '@/components/ui/Button';
import { Badge } from '@/components/ui/Badge';
import { Select } from '@/components/ui/Select';
import { RunStepsPanel } from '@/features/admin/RunStepsPanel';
import { AutoRunPanel } from '@/features/admin/AutoRunPanel';
import { formatMs } from '@/features/admin/formatMs';
import styles from '@/features/admin/PipelineRunsPage.module.css';

const RUNS_LIMIT = 10;

// Backend options shared with the config page — one entry per unique data
// backend, labelled by cluster (see features/admin/backends.ts).

function fmtStarted(iso: string): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  return d.toLocaleString(undefined, { dateStyle: 'medium', timeStyle: 'short' });
}

// Reuse the existing Badge palette: green=completed, blue=running,
// red=failed/timeout, muted=stopped-by-admin (deliberate, not broken).
function statusVariant(status: PipelineRun['status']): 'active' | 'admin' | 'inactive' | 'client' {
  if (status === 'completed') return 'active';
  if (status === 'running') return 'admin';
  if (status === 'stopped') return 'client';
  return 'inactive';
}

export function AdminPipelineRunsPage() {
  const auth = useAuth();
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const backends = useMemo(backendOptions, []);
  const [baseUrl, setBaseUrl] = useState(backends[0]?.baseUrl ?? '');
  const [expanded, setExpanded] = useState<string | null>(null);

  // Retired pipelines drop out of the "Run now" dropdown too — same schedule
  // query (and cache key) the Automatic-runs panel uses. While the schedule is
  // loading/unavailable, fall back to showing every config for this backend.
  const scheduleQuery = useQuery({
    queryKey: ['admin', 'pipeline-schedule', baseUrl],
    queryFn: () => listPipelineSchedule(baseUrl),
    enabled: baseUrl.length > 0,
  });

  // Reports this backend can (re)generate — drives the "Run now" control.
  // Two exclusions: `frozen` configs (pipeline retired in code — the World
  // Cup reports and AH — their frozen payload must never be regenerated) and
  // pipelines retired at runtime via the schedule flag. An ended campaign that
  // is not frozen can still be re-run.
  const backendReports = useMemo(() => {
    const configs = DASHBOARD_CONFIGS.filter((c) => c.apiBaseUrl === baseUrl && !c.frozen);
    const pipelines = scheduleQuery.data?.pipelines;
    const retiredReportIds = new Set((pipelines ?? []).filter((p) => p.retired && p.report_id).map((p) => p.report_id));
    // A dashboard can ship before its pipeline (Monopoly 2026 did). Once the
    // schedule has loaded, only offer reports the backend actually knows how to
    // generate — "Run now" on the others can only fail.
    const knownReportIds = pipelines ? new Set(pipelines.filter((p) => p.report_id).map((p) => p.report_id)) : null;
    return configs.filter((c) => !retiredReportIds.has(c.reportId) && (knownReportIds === null || knownReportIds.has(c.reportId)));
  }, [baseUrl, scheduleQuery.data]);
  const [runReportId, setRunReportId] = useState('');
  // Guard against a selection that got retired after being picked.
  const selectedReportId = backendReports.some((c) => c.reportId === runReportId) ? runReportId : (backendReports[0]?.reportId ?? '');

  const runsKey = ['admin', 'pipeline-runs', baseUrl, RUNS_LIMIT] as const;
  const runsQuery = useQuery({
    queryKey: runsKey,
    queryFn: () => listPipelineRuns(baseUrl, RUNS_LIMIT),
    enabled: baseUrl.length > 0,
    /*
     * Poll fast while something is running (the step list under an expanded row
     * grows live), slow when everything has settled — 30s was too coarse to
     * watch a run progress.
     */
    refetchInterval: (query) => (query.state.data?.some((r) => r.status === 'running') ? 3_000 : 30_000),
  });

  const startRun = useMutation({
    mutationFn: () => triggerPipeline(baseUrl, selectedReportId),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: runsKey }),
  });

  const stopRun = useMutation({
    mutationFn: (runId: string) => stopPipelineRun(baseUrl, runId),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: runsKey }),
  });

  function onSignOut() {
    clearAuth();
    queryClient.clear();
    navigate('/login', { replace: true });
  }

  return (
    <div className={styles.page}>
      <div className={styles.bar}>
        <div>
          <div className={styles.barTitle}>Report generations</div>
          <div className={styles.barSub}>IceMobile admin · {auth?.clientId ?? ''}</div>
        </div>
        <div className={styles.barActions}>
          <Button variant="link" onClick={() => navigate('/admin/config')}>
            Pipeline config
          </Button>
          <Button variant="link" onClick={() => navigate('/admin/users')}>
            User management
          </Button>
          <Button variant="link" onClick={() => navigate('/')}>
            ← Back to reports
          </Button>
          <Button variant="link" onClick={onSignOut}>
            Sign out
          </Button>
        </div>
      </div>

      <div className={styles.body}>
        <div className={styles.toolbar}>
          <Select label="Backend" value={baseUrl} onChange={(e) => (setBaseUrl(e.target.value), setExpanded(null), setRunReportId(''))}>
            {backends.map((b) => (
              <option key={b.baseUrl} value={b.baseUrl}>
                {b.label}
              </option>
            ))}
          </Select>
          <div className={styles.runControls}>
            <Select label="Pipeline" value={selectedReportId} onChange={(e) => setRunReportId(e.target.value)}>
              {backendReports.map((c) => (
                <option key={c.reportId} value={c.reportId}>
                  {c.label}
                </option>
              ))}
            </Select>
            <Button variant="primary" onClick={() => startRun.mutate()} disabled={startRun.isPending || !selectedReportId}>
              {startRun.isPending ? 'Starting…' : 'Run now'}
            </Button>
            <Button variant="ghost" onClick={() => runsQuery.refetch()} disabled={runsQuery.isFetching}>
              {runsQuery.isFetching ? 'Refreshing…' : 'Refresh'}
            </Button>
          </div>
        </div>

        {startRun.isError && <Alert variant="error">{(startRun.error as AdminPipelinesApiError)?.message ?? 'Could not start the pipeline'}</Alert>}
        {stopRun.isError && <Alert variant="error">{(stopRun.error as AdminPipelinesApiError)?.message ?? 'Could not stop the run'}</Alert>}

        <AutoRunPanel baseUrl={baseUrl} />

        <div className={styles.tableWrap}>
          {runsQuery.isLoading ? (
            <div className={styles.state}>Loading runs…</div>
          ) : runsQuery.isError ? (
            <div className={styles.state}>Could not load runs: {(runsQuery.error as AdminPipelinesApiError)?.message ?? 'unknown error'}</div>
          ) : (runsQuery.data?.length ?? 0) === 0 ? (
            <div className={styles.state}>No report generations recorded on this backend yet.</div>
          ) : (
            <table className={styles.table}>
              <thead>
                <tr>
                  <th aria-label="expand" />
                  <th>Pipeline</th>
                  <th>Report</th>
                  <th>Trigger</th>
                  <th>Started</th>
                  <th>Duration</th>
                  <th>Status</th>
                  <th>Error</th>
                  <th aria-label="actions" />
                </tr>
              </thead>
              <tbody>
                {runsQuery.data!.map((run) => (
                  <Fragment key={run.id}>
                    <tr className={styles.runRow} onClick={() => setExpanded(expanded === run.id ? null : run.id)}>
                      <td>
                        <span className={styles.caret}>{expanded === run.id ? '▾' : '▸'}</span>
                      </td>
                      <td className={styles.pipelineName}>{run.pipeline_name}</td>
                      <td className={styles.muted}>{run.report_id ?? '—'}</td>
                      <td className={styles.muted}>
                        {run.triggered_by}
                        {run.spawned_by && (
                          <div className={styles.spawnedBy} title="the API process (or admin) that spawned this run">
                            {run.spawned_by}
                          </div>
                        )}
                      </td>
                      <td className={styles.muted}>{fmtStarted(run.started_at)}</td>
                      <td className={styles.mono}>{formatMs(run.duration_ms)}</td>
                      <td>
                        <Badge variant={statusVariant(run.status)}>{run.status}</Badge>
                      </td>
                      <td>
                        <div className={styles.runError} title={run.error ?? undefined}>
                          {run.error ?? ''}
                        </div>
                      </td>
                      <td>
                        {run.status === 'running' && (
                          <Button
                            variant="ghost"
                            disabled={stopRun.isPending}
                            onClick={(e) => {
                              e.stopPropagation(); // don't toggle the row expansion
                              stopRun.mutate(run.id);
                            }}
                          >
                            {stopRun.isPending && stopRun.variables === run.id ? 'Stopping…' : 'Stop'}
                          </Button>
                        )}
                      </td>
                    </tr>
                    {expanded === run.id && (
                      <tr>
                        <td colSpan={9} className={styles.stepsCell}>
                          <RunStepsPanel baseUrl={baseUrl} runId={run.id} />
                        </td>
                      </tr>
                    )}
                  </Fragment>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </div>
    </div>
  );
}
